import {useEffect, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useConfiguracionDeSeguridad, useGuardarConfiguracion} from '../api/sesion';
import {AYUDA_CONFIGURACION as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {tr} from '../i18n/idioma';

// Configuracion de seguridad (solo administradores).
export function PaginaConfiguracion() {
  const configuracion = useConfiguracionDeSeguridad();
  const guardar = useGuardarConfiguracion();
  const [dosPasos, setDosPasos] = useState(false);
  const [horas, setHoras] = useState(12);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [hecho, setHecho] = useState(false);

  useEffect(() => {
    if (configuracion.data) {
      setDosPasos(configuracion.data.dosPasosObligatorioAdministradores);
      setHoras(configuracion.data.horasDeSesion);
    }
  }, [configuracion.data]);

  const enviar = async () => {
    setMensaje(null);
    setHecho(false);
    try {
      await guardar.mutateAsync({dosPasosObligatorioAdministradores: dosPasos, horasDeSesion: horas});
      setHecho(true);
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  return (
    <section className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Configuración', 'Settings')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Ajustes de seguridad de la aplicación. Cada cambio queda en la bitácora.',
            'Security settings of the application. Every change is recorded in the audit log.',
          )}
        </p>
      </div>
      <form
        className="space-y-4 rounded-lg border border-linea bg-white p-5"
        onSubmit={e => {
          e.preventDefault();
          void enviar();
        }}
      >
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={dosPasos}
            onChange={e => setDosPasos(e.target.checked)}
            className="mt-1 accent-verde"
          />
          <span>
            <span className="font-medium">
              {tr(
                'Exigir la verificación en dos pasos a los administradores',
                'Require two-step verification for administrators',
              )}
              <NotaDeAyuda texto={A.dosPasos} dentroDeEtiqueta />
            </span>
            <span className="mt-0.5 block text-xs text-apagado">
              {tr(
                'Al activarlo, cada administrador que entre sin tenerla deberá configurarla antes de seguir (con una app de autenticación en su teléfono). Recomendado: los administradores pueden crear usuarios y ver la bitácora.',
                'When on, every administrator who signs in without it must set it up before continuing (with an authenticator app on their phone). Recommended: administrators can create users and see the audit log.',
              )}
            </span>
          </span>
        </label>
        <label className="block text-sm">
          <span className="font-medium">
            {tr('Horas de sesión sin actividad', 'Session hours without activity')}
            <NotaDeAyuda texto={A.horas} dentroDeEtiqueta />
          </span>
          <span className="mt-0.5 block text-xs text-apagado">
            {tr(
              'Pasado ese tiempo sin usar la aplicación hay que volver a entrar. Entre 1 y 168 horas (una semana).',
              'After that long without using the application you must sign in again. Between 1 and 168 hours (one week).',
            )}
          </span>
          <input
            type="number"
            min={1}
            max={168}
            value={horas}
            onChange={e => setHoras(Number(e.target.value))}
            className="mt-1 w-32 rounded-md border border-linea px-3 py-2 text-sm text-tinta"
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={guardar.isPending || configuracion.isPending}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
          </button>
          {mensaje && <span className="text-sm text-alerta">{mensaje}</span>}
          {hecho && <span className="text-sm text-verde-oscuro">{tr('Guardado.', 'Saved.')}</span>}
        </div>
      </form>
      <div className="rounded-lg border border-linea bg-white p-5 text-sm">
        <h2 className="font-medium">{tr('Cómo funciona el acceso', 'How access works')}</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-apagado">
          <li>
            {tr(
              'Las contraseñas se guardan cifradas (Argon2id); nadie, ni el administrador, puede verlas.',
              'Passwords are stored hashed (Argon2id); nobody, not even the administrator, can see them.',
            )}
          </li>
          <li>
            {tr(
              'Cinco intentos fallidos seguidos bloquean la cuenta 15 minutos.',
              'Five failed attempts in a row lock the account for 15 minutes.',
            )}
          </li>
          <li>
            {tr(
              'Las contraseñas nuevas tienen al menos 10 caracteres con letras y números.',
              'New passwords have at least 10 characters with letters and numbers.',
            )}
          </li>
          <li>
            {tr(
              'Cambiar de rol o desactivar a alguien cierra sus sesiones al instante.',
              'Changing someone’s role or deactivating them closes their sessions immediately.',
            )}
          </li>
          <li>
            {tr(
              'Las claves de las navieras y de la nube no se administran aquí: van en la configuración privada del servidor.',
              'The shipping line and cloud keys are not managed here: they go in the server’s private configuration.',
            )}
          </li>
        </ul>
      </div>
    </section>
  );
}
